/**
 * Step 2 — Contractor
 *
 * Contractor data block: 9 fields (3 required, 6 optional). The schedule
 * (planned dates + traffic impact) moved to the basic-info step.
 */
import { memo } from 'react';
import { InputBase, Stack } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { IMaskInput } from 'react-imask';
import { useTranslation } from 'react-i18next';

import { AppTextInput } from '@/components/common/AppTextInput';
import { AppTextarea } from '@/components/common/AppTextarea';
import { FormSection } from '@/components/common/form';
import {
  OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH,
  PHONE_NUMBER_MASK,
} from '@/constants/obstructions';
import { TEXTAREA_MAX_ROWS, TEXTAREA_MIN_ROWS } from '@/constants/ui';

import type { ObstructionWizardFormState } from '../../types';

import styles from './Step2Contractor.module.css';

interface Step2ContractorProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading: boolean;
}

export const Step2Contractor = memo(function Step2Contractor({
  form,
  isLoading,
}: Step2ContractorProps) {
  const { t } = useTranslation();

  return (
    <Stack gap="lg">
      <FormSection
        title={t('obstructions.wizard.step2.contractorHeading')}
        description={t('obstructions.wizard.step2.contractorDescription')}
      >
        <AppTextInput
          label={t('obstructions.wizard.fields.contractorCompany')}
          placeholder={t('obstructions.wizard.placeholders.contractorCompany')}
          required
          disabled={isLoading}
          autoComplete="organization"
          maxLength={OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH}
          mb="md"
          value={form.values.contractorCompany}
          onChange={(event) =>
            form.setFieldValue('contractorCompany', event.currentTarget.value)
          }
          error={form.errors.contractorCompany}
        />

        <div className={styles.twoColumnRow}>
          <AppTextInput
            label={t('obstructions.wizard.fields.contractorManager')}
            placeholder={t(
              'obstructions.wizard.placeholders.contractorManager'
            )}
            required
            disabled={isLoading}
            autoComplete="name"
            maxLength={OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH}
            value={form.values.contractorManager}
            onChange={(event) =>
              form.setFieldValue('contractorManager', event.currentTarget.value)
            }
            error={form.errors.contractorManager}
          />
          <InputBase
            component={IMaskInput}
            mask={PHONE_NUMBER_MASK}
            unmask
            label={t('obstructions.wizard.fields.contractorPhone')}
            placeholder={t('obstructions.wizard.placeholders.contractorPhone')}
            required
            disabled={isLoading}
            autoComplete="tel"
            value={form.values.contractorPhone}
            onAccept={(value: string) =>
              form.setFieldValue('contractorPhone', value)
            }
            error={form.errors.contractorPhone}
          />
        </div>

        <div className={styles.twoColumnRow}>
          <AppTextInput
            label={t('obstructions.wizard.fields.contractorEmail')}
            placeholder={t('obstructions.wizard.placeholders.contractorEmail')}
            disabled={isLoading}
            autoComplete="email"
            type="email"
            maxLength={OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH}
            value={form.values.contractorEmail}
            onChange={(event) =>
              form.setFieldValue('contractorEmail', event.currentTarget.value)
            }
            error={form.errors.contractorEmail}
          />
          <InputBase
            component={IMaskInput}
            mask={PHONE_NUMBER_MASK}
            unmask
            label={t('obstructions.wizard.fields.contractorEmergencyContact')}
            placeholder={t(
              'obstructions.wizard.placeholders.contractorEmergencyContact'
            )}
            disabled={isLoading}
            autoComplete="tel"
            value={form.values.contractorEmergencyContact}
            onAccept={(value: string) =>
              form.setFieldValue('contractorEmergencyContact', value)
            }
            error={form.errors.contractorEmergencyContact}
          />
        </div>

        <AppTextInput
          label={t('obstructions.wizard.fields.contractorPermitNumber')}
          placeholder={t(
            'obstructions.wizard.placeholders.contractorPermitNumber'
          )}
          disabled={isLoading}
          autoComplete="off"
          maxLength={OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH}
          mb="md"
          value={form.values.contractorPermitNumber}
          onChange={(event) =>
            form.setFieldValue(
              'contractorPermitNumber',
              event.currentTarget.value
            )
          }
          error={form.errors.contractorPermitNumber}
        />

        <div className={styles.twoColumnRow}>
          <DatePickerInput
            label={t('obstructions.wizard.fields.contractorPermitIssuedDate')}
            placeholder={t(
              'obstructions.wizard.placeholders.contractorPermitIssuedDate'
            )}
            disabled={isLoading}
            clearable
            value={form.values.contractorPermitIssuedDate || null}
            onChange={(value) =>
              form.setFieldValue('contractorPermitIssuedDate', value ?? '')
            }
            error={form.errors.contractorPermitIssuedDate}
          />
          <DatePickerInput
            label={t('obstructions.wizard.fields.contractorPermitExpiryDate')}
            placeholder={t(
              'obstructions.wizard.placeholders.contractorPermitExpiryDate'
            )}
            disabled={isLoading}
            clearable
            value={form.values.contractorPermitExpiryDate || null}
            onChange={(value) =>
              form.setFieldValue('contractorPermitExpiryDate', value ?? '')
            }
            error={form.errors.contractorPermitExpiryDate}
          />
        </div>

        <AppTextarea
          label={t('obstructions.wizard.fields.contractorNotes')}
          placeholder={t('obstructions.wizard.placeholders.contractorNotes')}
          disabled={isLoading}
          autosize
          minRows={TEXTAREA_MIN_ROWS}
          maxRows={TEXTAREA_MAX_ROWS}
          maxLength={OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH}
          value={form.values.contractorNotes}
          onChange={(event) =>
            form.setFieldValue('contractorNotes', event.currentTarget.value)
          }
          error={form.errors.contractorNotes}
        />
      </FormSection>
    </Stack>
  );
});
